import { useMutation } from "@apollo/client/react";
import {
  CreateStudentFeedback,
  CreateStudentFeedbackVariables,
  FeedbackGivenOnType,
} from "__generated__/types";
import { Button, TextButton } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { SubmitButton } from "common/components/forms/SubmitButton";
import { useToggle } from "common/hooks/useToggle";
import { markdownInput } from "common/input-types";
import gql from "graphql-tag";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";

const MUTATION = gql`
  mutation CreateStudentFeedback($input: CreateStudentFeedbackInput!) {
    createStudentFeedback(input: $input) {
      feedback {
        id
      }
    }
  }
`;

interface FormValues {
  feedback: string;
}

interface Props {
  givenOnReferenceId: string;
  givenOnType: FeedbackGivenOnType;
}

export const FeedbackForm: React.FC<React.PropsWithChildren<Props>> = ({
  givenOnReferenceId,
  givenOnType,
}) => {
  const [createFeedback, setCreateFeedback] = useToggle(false);
  const methods = useForm<FormValues>();
  const { handleSubmit } = methods;

  const [mutate, { data, loading, error }] = useMutation<
    CreateStudentFeedback,
    CreateStudentFeedbackVariables
  >(MUTATION);

  const onSubmit = async (data: FormValues) => {
    await mutate({
      variables: {
        input: {
          givenOnReferenceId,
          givenOnType,
          body: markdownInput(data.feedback),
        },
      },
    });
  };

  if (data) {
    return (
      <p>Takk for din tilbakemelding! Vi vil svare deg så fort som mulig.</p>
    );
  }

  return (
    <>
      {!createFeedback && (
        <TextButton kind="secondary" onClick={setCreateFeedback}>
          Gi oss tilbakemelding
        </TextButton>
      )}

      {createFeedback ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <MarkdownField<FormValues>
              name="feedback"
              title="Din tilbakemelding:"
              required={true}
              maxWidth="400px"
            />

            <MutationLoadingOrError loading={loading} error={error} />

            <p>
              <Button kind="secondary" onClick={setCreateFeedback}>
                Avbryt
              </Button>
              <SubmitButton>Send inn</SubmitButton>
            </p>
          </form>
        </FormProvider>
      ) : null}
    </>
  );
};
